import { Controller } from "@hotwired/stimulus"

// po-remap-confirm — intercepts the Create-Order submit on the PO review
// page and, if any rows are remapping an existing learned term to a
// different product, surfaces a one-shot dialog so the user can:
//   - keep the remap for ALL future POs (default — fires ProductMatcher.learn),
//   - or keep it as a one-off for THIS PO only (sets items[N][skip_learn]=1
//     so the server suppresses the learn call for that row).
//
// Rows that are remapping are detected by comparing the product select's
// current value against `data-po-row-alias-product-id` (set server-side
// from the existing learned MatchingRule). If no rows are remapping, we just let
// the submit proceed (Rails' turbo_confirm on the form still runs).
//
// Note: this controller does NOT replace the form's existing turbo_confirm
// (the final "Create order with these details?" prompt). It runs BEFORE
// that — gather decisions for any remaps, then re-submit the form which
// then hits turbo_confirm and proceeds normally.
export default class extends Controller {
    static targets = ["dialog", "rowList"]

    connect() {
        this._userConfirmed = false
        this.element.addEventListener("submit", this.onSubmit.bind(this))
    }

    onSubmit(event) {
        if (this._userConfirmed) return

        const remaps = this.collectRemaps()
        if (remaps.length === 0) return // nothing to confirm; let submit proceed

        event.preventDefault()
        this.showDialog(remaps)
    }

    collectRemaps() {
        const rows = this.element.querySelectorAll('[data-controller~="po-row"]')
        const remaps = []
        rows.forEach((row, idx) => {
            const aliasProductId = row.getAttribute("data-po-row-alias-product-id")
            const aliasTerm      = row.getAttribute("data-po-row-alias-term")
            if (!aliasProductId) return // no prior alias for this row

            const select = row.querySelector('select[name*="[product_id]"]')
            if (!select) return

            const currentId = select.value
            // Only a "remap" if the user picked a DIFFERENT product than the alias
            // already points at. Same product = no remap. Empty = "Create new" =
            // also a remap of intent (the user wants to break the alias).
            if (currentId === aliasProductId) return

            const newProductLabel = (select.options[select.selectedIndex]?.text || "(new product)").trim()
            const oldProductLabel = this.lookupProductName(aliasProductId) || "(prior product)"

            remaps.push({
                rowIdx: idx,
                row: row,
                term: aliasTerm || "(unknown term)",
                oldProductLabel: oldProductLabel,
                newProductLabel: newProductLabel
            })
        })
        return remaps
    }

    // Look up a product's display name from any select option that has its id.
    // Cheap fallback when we don't have a server-rendered name handy.
    lookupProductName(productId) {
        const opt = this.element.querySelector(`select[name*="[product_id]"] option[value="${CSS.escape(productId)}"]`)
        return opt ? opt.text.trim() : null
    }

    showDialog(remaps) {
        if (!this.hasDialogTarget) return

        const html = remaps.map((r, i) => `
            <div style="padding: 0.625rem 0.75rem; border: 1px solid var(--border-light, #e5e7eb); border-radius: 0.375rem; margin-bottom: 0.5rem; background: var(--bg-surface, #fff);">
              <div style="font-weight: 600; font-size: 0.875rem; color: var(--text-primary, #111);">
                "${this.escapeHtml(r.term)}"
              </div>
              <div style="font-size: 0.8125rem; color: var(--text-secondary, #555); margin-top: 0.25rem;">
                ${this.escapeHtml(r.oldProductLabel)}
                <span style="color: var(--text-tertiary, #999); margin: 0 0.25rem;">→</span>
                <strong style="color: var(--text-primary, #111);">${this.escapeHtml(r.newProductLabel)}</strong>
              </div>
              <label style="display: flex; align-items: center; gap: 0.5rem; margin-top: 0.5rem; font-size: 0.8125rem; color: var(--text-secondary, #555); cursor: pointer;">
                <input type="checkbox" data-remap-row-idx="${i}" data-action="change->po-remap-confirm#toggleOneOff" />
                Keep this as a one-off for this PO only (don't update the saved mapping)
              </label>
            </div>
        `).join("")

        this.rowListTarget.innerHTML = html
        this._pendingRemaps = remaps
        if (typeof this.dialogTarget.showModal === "function") {
            this.dialogTarget.showModal()
        } else {
            // Fallback for browsers without <dialog> support.
            this.dialogTarget.style.display = "block"
        }
    }

    toggleOneOff(event) {
        const idx = parseInt(event.target.getAttribute("data-remap-row-idx"), 10)
        const remap = this._pendingRemaps?.[idx]
        if (!remap) return
        const skipLearn = remap.row.querySelector('input[name*="[skip_learn]"]')
        if (skipLearn) skipLearn.value = event.target.checked ? "1" : "0"
    }

    cancelRemap(event) {
        if (event) event.preventDefault()
        if (this.hasDialogTarget && typeof this.dialogTarget.close === "function") {
            this.dialogTarget.close()
        }
        // Reset all skip_learn flags so any "Keep one-off" picks don't bleed
        // into a future submit attempt.
        ;(this._pendingRemaps || []).forEach(r => {
            const skipLearn = r.row.querySelector('input[name*="[skip_learn]"]')
            if (skipLearn) skipLearn.value = "0"
        })
    }

    confirmRemap(event) {
        if (event) event.preventDefault()
        if (this.hasDialogTarget && typeof this.dialogTarget.close === "function") {
            this.dialogTarget.close()
        }
        this._userConfirmed = true
        // Re-fire submit. Rails' turbo_confirm on the form will run next.
        this.element.requestSubmit
            ? this.element.requestSubmit()
            : this.element.submit()
    }

    escapeHtml(s) {
        return String(s).replace(/[&<>"']/g, c => (
            { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
        ))
    }
}
